/**
 * Collapses a password sign-in rejection into the fixed set of causes the UI is
 * allowed to name.
 *
 * Supabase's own `error.message` is never rendered. It is untranslated, it
 * changes with the backend version, and it distinguishes "invalid credentials"
 * from "email not confirmed" and "user banned" - a difference that tells an
 * unauthenticated caller whether an address has an account here. Every
 * credential-shaped rejection therefore collapses to one indistinguishable
 * outcome, and only transport and rate-limit failures (which say nothing about
 * the account) get their own copy.
 */

export type PasswordSignInErrorKey =
  | 'wrongCredentials'
  | 'networkError'
  | 'rateLimited'
  | 'generic';

/** i18n keys in the `auth` namespace, one per classified cause. */
export const PASSWORD_SIGN_IN_ERROR_KEYS = {
  wrongCredentials: 'passwordSignIn.wrongCredentials',
  networkError: 'passwordSignIn.networkError',
  rateLimited: 'passwordSignIn.rateLimited',
  generic: 'passwordSignIn.genericError',
} as const satisfies Record<PasswordSignInErrorKey, string>;

interface AuthFailureShape {
  readonly status?: unknown;
  readonly code?: unknown;
  readonly name?: unknown;
  readonly message?: unknown;
}

const TOO_MANY_REQUESTS = 429;

/** Every status Supabase Auth uses to reject a credential pair. */
const CREDENTIAL_REJECTION_STATUSES: ReadonlySet<number> = new Set([400, 401, 403, 422]);

/**
 * Credential rejections that reach us without a status - a mocked client, a
 * proxy that strips it, or a transport that surfaced the body only.
 */
const CREDENTIAL_CODES: ReadonlySet<string> = new Set([
  'invalid_credentials',
  'email_not_confirmed',
  'user_banned',
  'invalid_grant',
]);

/**
 * Lower-cased fragments of the message browsers use for a failed fetch. Plain
 * substring scans, never a regex: this runs on attacker-influenced text and the
 * repo bans backtracking patterns on untrusted input.
 */
const TRANSPORT_MESSAGE_FRAGMENTS = [
  'failed to fetch',
  'load failed',
  'networkerror',
  'network request failed',
];

function toFailureShape(error: unknown): AuthFailureShape {
  return typeof error === 'object' && error !== null ? (error as AuthFailureShape) : {};
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function isTransportFailure(name: string, code: string, message: string): boolean {
  if (name === 'AuthRetryableFetchError' || code === 'request_timeout') {
    return true;
  }
  const lowered = message.toLowerCase();
  return TRANSPORT_MESSAGE_FRAGMENTS.some((fragment) => lowered.includes(fragment));
}

export function classifyPasswordSignInError(error: unknown): PasswordSignInErrorKey {
  const { status, code, name, message } = toFailureShape(error);
  const codeText = asText(code);

  if (status === TOO_MANY_REQUESTS || codeText.includes('rate_limit')) {
    return 'rateLimited';
  }
  if (isTransportFailure(asText(name), codeText, asText(message))) {
    return 'networkError';
  }
  if (typeof status === 'number' && CREDENTIAL_REJECTION_STATUSES.has(status)) {
    return 'wrongCredentials';
  }
  if (CREDENTIAL_CODES.has(codeText)) {
    return 'wrongCredentials';
  }
  return 'generic';
}
